import { FC, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, SimpleGrid, Skeleton } from '@chakra-ui/react';

import useCurrentPath from 'common/hooks/useCurrentPath';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineTag from 'components/_core/SunshineTag';
import ClientAvatar from 'features/Work/pages/Clients/components/ClientAvatar';

import { ClientFromList } from '../../hooks/queries/useGetClients';
import ListItemMenu from './components/ListItemMenu';
import { useGetClientStatusOverview } from './hooks/mutations/useClientStatusOverview';
import locales from './locales';
import { getTotalCountOf } from './utils';

export interface ListItemProps {
  item: ClientFromList;
}

const ListItem: FC<ListItemProps> = ({ item }) => {
  const history = useHistory();
  const { path } = useCurrentPath();

  const { clientId, name } = item;

  const { loading, statusOverview } = useGetClientStatusOverview(clientId);

  const invoices = statusOverview?.invoices;
  const quotes = statusOverview?.quotes;

  const overdueInvoiceCount = invoices?.overdue?.count || 0;

  const invoiceCount = useMemo(
    () => (invoices ? getTotalCountOf(invoices) : 0),
    [invoices],
  );
  const quoteCount = useMemo(
    () => (quotes ? getTotalCountOf(quotes) : 0),
    [quotes],
  );

  const invoiceAndQuoteCount = invoiceCount + quoteCount;

  const invoicesCount = locales.formatString(
    invoiceCount > 1 ? locales.invoices : locales.invoice,
    invoiceCount,
  );

  const quotesCount = locales.formatString(
    quoteCount > 1 ? locales.quotes : locales.quote,
    quoteCount,
  );

  const description = useMemo(() => {
    if (invoiceAndQuoteCount === 0) {
      return locales.noDocumentsYet;
    }
    return `${quotesCount}, ${invoicesCount}`;
  }, [invoiceAndQuoteCount, invoicesCount, quotesCount]);

  return (
    <SunshineCard
      alignItems="center"
      flexDirection="row"
      onClick={() => history.push(`${path}/${clientId}`)}
    >
      <SunshineCard.Slot name="aside">
        <ClientAvatar name={name} />
      </SunshineCard.Slot>

      <SimpleGrid
        alignItems="center"
        columns={2}
        spacing="space-24"
        width="100%"
      >
        <SunshineCard.Content>
          <SunshineCard.Title fontWeight="normal">{name}</SunshineCard.Title>
          <SunshineCard.Text>
            <Skeleton isLoaded={!loading}>{description}</Skeleton>
          </SunshineCard.Text>
        </SunshineCard.Content>

        {overdueInvoiceCount ? (
          <Box flex={1}>
            <SunshineTag colorScheme="red" flexShrink={0} size="sm">
              {
                locales.formatString(
                  overdueInvoiceCount > 1
                    ? locales.overdueInvoices
                    : locales.overdueInvoice,
                  overdueInvoiceCount,
                ) as string
              }
            </SunshineTag>
          </Box>
        ) : null}
      </SimpleGrid>

      <SunshineCard.Slot name="action">
        <ListItemMenu client={item} />
      </SunshineCard.Slot>
    </SunshineCard>
  );
};

export default ListItem;
